import { useTranslation } from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants, Modal } from "@next-platform/client-common-spicy-ui";
import { useCallback, useEffect, useRef, useState } from "react";

import { Row, SpicyButton, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import useApi from "@/components/useApi";

import ConversationMemoriesDropdown from "./ConversationMemoriesDropdown";
import { filterRemainingMemoriesAfterDeleteAll } from "./deleteAllMemoriesUtils";
import MemoryEditor from "./MemoryEditor";
import ModalDeleteAllMemories from "./ModalDeleteAllMemories";

type ModalConversationMemoriesProps = {
  dataTestId?: string;
  isOpen: boolean;
  setIsOpen: (show: boolean) => void;
  conversationId: string;
};

type Memory = {
  id: string;
  content: string;
  is_pinned: boolean;
  is_last_id?: boolean;
  memory_type: string;
  createdAt: number;
};

export default function ModalConversationMemories({ conversationId, dataTestId = "", isOpen, setIsOpen }: ModalConversationMemoriesProps) {
  const { t } = useTranslation();
  const [memories, setMemories] = useState<Memory[]>([]);
  const [showAddMemory, setShowAddMemory] = useState(false);
  const [currentlyEditing, setCurrentlyEditing] = useState("");
  const { callApi, jsonApiService, isLoading } = useApi();
  const { callApi: callUpdateApi, isLoading: isUpdateLoading } = useApi();
  const lastMemoryIdRef = useRef<string | null>(null);
  const [showDeleteAllMemories, setShowDeleteAllMemories] = useState(false);

  const fetchMemories = useCallback(
    async (isInitialLoad = false) => {
      let resource = `${conversationId}/memories`;

      if (!isInitialLoad && lastMemoryIdRef.current) {
        resource += `?last_id=${lastMemoryIdRef.current}`;
      }

      const result = (await callApi(jsonApiService.fetchResource, "conversations", resource)) as Memory[] | undefined;

      if (result) {
        if (isInitialLoad) {
          setMemories(result);
        } else {
          setMemories((prev) => [...prev, ...result]);
        }
        if (result.length) {
          lastMemoryIdRef.current = result[result.length - 1].is_last_id ? result[result.length - 1].id : null;
        }
      }
    },
    [conversationId, callApi, jsonApiService.fetchResource],
  );

  // Effect for initial load
  useEffect(() => {
    if (!conversationId) {
      return;
    }

    fetchMemories(true);
  }, [conversationId, fetchMemories]);

  const onCancel = () => {
    setShowAddMemory(false);
    setCurrentlyEditing("");
  };

  const saveMemory = async (memoryId: string, memoryText: string) => {
    if (memoryText === "") {
      return;
    }

    if (memoryId !== "new") {
      await callUpdateApi(jsonApiService.updateResource, `memories`, memoryId, {
        content: memoryText,
      }).then(() => {
        //we update the memory in the list wiith memoryId
        const newMemories = memories.map((memory) => {
          if (memory.id === memoryId) {
            memory.content = memoryText;
          }
          return memory;
        });
        setMemories(newMemories);
        setCurrentlyEditing("");
      });
    } else {
      //we create a new memory
      const newMemory = (await callUpdateApi(jsonApiService.createResource, `conversations/${conversationId}/memories`, { content: memoryText })) as
        | Memory
        | undefined;
      if (newMemory) setMemories([newMemory, ...memories]);
      setShowAddMemory(false);
      setCurrentlyEditing("");
    }
  };

  const handleShowAddMemory = () => {
    setShowAddMemory(true);
    setCurrentlyEditing("new");
  };

  const deleteMemory = async (memoryId: string) => {
    const oldMemories = [...memories];

    const newMemories = memories.filter((memory) => memory.id !== memoryId);

    setMemories(newMemories);

    await callUpdateApi(jsonApiService.deleteResource, `memories`, memoryId).catch(() => {
      setMemories(oldMemories);
    });
  };

  const deleteAllMemories = async (keepPinnedMemories: boolean, keepManualEntries: boolean) => {
    let resource = `conversations/${conversationId}/memories`;

    if (keepPinnedMemories) {
      resource += "?keep_pinned_memories=1";
    }

    if (keepManualEntries) {
      resource += keepPinnedMemories ? "&keep_user_memories=1" : "?keep_user_memories=1";
    }

    await callUpdateApi(jsonApiService.deleteResource, resource).then(() => {
      setMemories(filterRemainingMemoriesAfterDeleteAll(memories, keepPinnedMemories, keepManualEntries));

      lastMemoryIdRef.current = null;
    });
  };

  const handlePinnedToggle = async (memoryId: string, isPinned: boolean) => {
    await callUpdateApi(jsonApiService.updateResource, `memories`, memoryId, {
      is_pinned: isPinned,
    }).then(() => {
      const newMemories = memories.map((memory) => {
        if (memory.id === memoryId) {
          memory.is_pinned = isPinned;
        }
        return memory;
      });

      //we sort by createrAt descending but with pinned memories first
      newMemories.sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) {
          return -1;
        }
        if (!a.is_pinned && b.is_pinned) {
          return 1;
        }
        return b.createdAt - a.createdAt;
      });

      setMemories(newMemories);
    });
  };

  const ignoreUnavailableMemoryAction = useCallback(() => undefined, []);

  if (showDeleteAllMemories) {
    return (
      <ModalDeleteAllMemories
        isVisible={showDeleteAllMemories}
        isLoading={isUpdateLoading}
        onUserConfirmation={deleteAllMemories}
        handleClose={() => setShowDeleteAllMemories(false)}
      />
    );
  }

  return (
    <Modal
      title={t("chat:modal.memories.title")}
      open={isOpen}
      onOpenChange={setIsOpen}
      className="h-full"
      classNames={{ title: "text-center pl-[28px]" }}
      data-testid={dataTestId}
    >
      {!showAddMemory && !currentlyEditing && (
        <Row className="m-0" justify="end">
          <SpicyButton
            isIconOnly
            variant="tertiary_small_short"
            color="primary"
            startContent={<Icon icon={IconVariants.SQUARE_ADD} size={IconSize.SMALL} />}
            onPress={handleShowAddMemory}
          />

          <ConversationMemoriesDropdown
            onAdd={handleShowAddMemory}
            onClose={() => setIsOpen(false)}
            onDelete={() => setShowDeleteAllMemories(true)}
          />
        </Row>
      )}

      {showAddMemory ? (
        <MemoryEditor
          memoryId={"new"}
          isPinned={false}
          memoryType="user"
          onSave={saveMemory}
          onDelete={ignoreUnavailableMemoryAction}
          onCancel={onCancel}
          onPinnedToggle={ignoreUnavailableMemoryAction}
          currentlyEditing={currentlyEditing}
          setCurrentlyEditing={setCurrentlyEditing}
        />
      ) : null}

      {isLoading && !memories?.length ? (
        <Row className="leading-5 pb-0">
          <Text variant="sm">
            <Trans i18nKey="chat:modal.memories.loadingText" />
          </Text>
        </Row>
      ) : null}

      {!isLoading && !memories?.length && (
        <Row className="leading-5 pb-0">
          <Text variant="sm">
            <Trans i18nKey="chat:modal.memories.noMemoriesText" />
          </Text>
        </Row>
      )}

      {memories.map((memory, index) => (
        <MemoryEditor
          key={index}
          currentlyEditing={currentlyEditing}
          setCurrentlyEditing={setCurrentlyEditing}
          memoryId={memory?.id}
          memoryText={memory?.content}
          isPinned={memory?.is_pinned}
          memoryType={memory?.memory_type}
          onSave={saveMemory}
          onCancel={onCancel}
          onDelete={deleteMemory}
          onPinnedToggle={handlePinnedToggle}
        />
      ))}

      {lastMemoryIdRef.current ? (
        <Row className="leading-5 pb-0">
          <SpicyButton variant="secondary_short" color="neutral" onPress={() => fetchMemories()} isDisabled={isLoading}>
            <Text variant={"xs"}>
              {isLoading ? <Trans i18nKey="chat:modal.memories.loadingText" /> : <Trans i18nKey="chat:modal.memories.cta.loadMore" />}
            </Text>
          </SpicyButton>
        </Row>
      ) : null}
    </Modal>
  );
}
